import { COLORS } from '@/config/constants';
import { generateDotLayout } from '@/services/dotLayoutGenerator';
import type { DifficultyConfig, MathOperation, TileSpec } from '@/types/models';

const randInt = (min: number, max: number) =>
  Math.floor(Math.random() * (max - min + 1)) + min;

const shuffle = <T>(arr: T[]): T[] => {
  const a = arr.slice();
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

const pickColor = (used: Set<number>): number => {
  const palette = COLORS.tilePalette;
  for (let attempts = 0; attempts < 8; attempts++) {
    const c = palette[Math.floor(Math.random() * palette.length)];
    if (!used.has(c)) {
      used.add(c);
      return c;
    }
  }
  return palette[Math.floor(Math.random() * palette.length)];
};

export function spawnRound(op: MathOperation, cfg: DifficultyConfig): TileSpec[] {
  const counts = chooseDotCounts(op.result, cfg);
  const usedColors = new Set<number>();

  const tiles: TileSpec[] = counts.map((count, i) => ({
    id: `tile-${Date.now()}-${i}`,
    dotCount: count,
    dotPositions: generateDotLayout({ count }),
    color: pickColor(usedColors),
    isCorrect: count === op.result
  }));

  return shuffle(tiles);
}

function chooseDotCounts(correct: number, cfg: DifficultyConfig): number[] {
  const total = cfg.tileCount;
  const maxDots = cfg.maxDots;
  const counts = new Set<number>();
  counts.add(correct);

  const distractorPool: number[] = [];
  if (correct - 1 >= 1) distractorPool.push(correct - 1);
  if (correct + 1 <= maxDots) distractorPool.push(correct + 1);

  const distractors = Math.min(cfg.distractorCount, distractorPool.length);
  const shuffledDistractors = shuffle(distractorPool).slice(0, distractors);
  for (const d of shuffledDistractors) counts.add(d);

  let safety = 100;
  while (counts.size < total && safety-- > 0) {
    const candidate = randInt(1, maxDots);
    counts.add(candidate);
  }

  const result = Array.from(counts).slice(0, total);
  while (result.length < total) {
    result.push(randInt(1, maxDots));
  }
  return result;
}
